import { useEffect, useState } from "react";
import { http, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, HardDrives, ArrowsClockwise, PencilSimple, CheckCircle, XCircle, Stethoscope, MagicWand } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyForm = {
  name: "", vendor: "Huawei", model: "", ip_address: "", local_ip: "",
  location: "", latitude: 12.9716, longitude: 77.5946, total_ports: 16,
  nas_id: "", snmp_community: "public", snmp_version: "v2c", snmp_port: 161,
  snmp_via_nas: false,
};

export default function OltInventory() {
  const [olts, setOlts] = useState([]);
  const [nasList, setNasList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [syncing, setSyncing] = useState({});
  const [diag, setDiag] = useState(null); // {loading, data, olt}
  const [customOid, setCustomOid] = useState("");
  const [customCommunity, setCustomCommunity] = useState("");
  const [customWalk, setCustomWalk] = useState(null); // {loading, data}
  const runDiagnose = async (o) => {
    setDiag({ loading: true, olt: o, data: null });
    setCustomOid(""); setCustomWalk(null);
    try {
      const { data } = await http.get(`/olts/${o.id}/snmp-diagnose`);
      setDiag({ loading: false, olt: o, data });
    } catch (err) {
      setDiag({ loading: false, olt: o, data: { error: err?.response?.data?.detail || "diagnose failed" } });
    }
  };
  const runCustomWalk = async () => {
    if (!diag?.olt?.id || !customOid.trim()) return;
    setCustomWalk({ loading: true, data: null });
    try {
      const params = { root: customOid.trim(), max_rows: 40 };
      if (customCommunity.trim()) params.community = customCommunity.trim();
      const { data } = await http.get(`/olts/${diag.olt.id}/snmp-walk`, { params });
      setCustomWalk({ loading: false, data });
    } catch (err) {
      setCustomWalk({ loading: false, data: { ok: false, error: err?.response?.data?.detail || "walk failed" } });
    }
  };

  const load = async () => setOlts((await http.get("/olts")).data);
  useEffect(() => {
    load();
    http.get("/nas").then((r) => setNasList(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({
      name: o.name || "", vendor: o.vendor || "", model: o.model || "",
      ip_address: o.ip_address || "", local_ip: o.local_ip || "",
      location: o.location || "", latitude: o.latitude, longitude: o.longitude,
      total_ports: o.total_ports ?? 16,
      nas_id: o.nas_id || "",
      snmp_community: o.snmp_community || "public",
      snmp_version: o.snmp_version || "v2c",
      snmp_port: o.snmp_port ?? 161,
      snmp_via_nas: !!o.snmp_via_nas,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        total_ports: Number(form.total_ports),
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
        snmp_port: Number(form.snmp_port),
        nas_id: form.nas_id || null,
        local_ip: form.local_ip || null,
      };
      if (editing) await http.patch(`/olts/${editing.id}`, payload);
      else await http.post("/olts", payload);
      toast.success(editing ? "OLT updated" : "OLT registered");
      setOpen(false); setEditing(null); setForm(emptyForm);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove OLT?")) return;
    await http.delete(`/olts/${id}`); load();
  };

  const sync = async (o) => {
    setSyncing((s) => ({ ...s, [o.id]: true }));
    const t = toast.loading(`SNMP polling ${o.name} at ${o.local_ip || o.ip_address}…`);
    try {
      const { data } = await http.post(`/olts/${o.id}/sync`);
      toast.dismiss(t);
      if (data.reachable) {
        toast.success(`${o.name} reachable · ${data.sys_name || data.sys_descr?.slice(0, 40) || "OK"} · uptime ${data.sys_uptime || "—"}`);
      } else {
        toast.error(`${o.name} unreachable: ${data.error || "unknown"}`);
      }
      load();
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing((s) => ({ ...s, [o.id]: false }));
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="olt-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">Access-network hardware</div>
          <h1 className="font-display font-bold text-4xl mt-1">OLT Inventory</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Register each Huawei/ZTE/Nokia/Fiberhome OLT and link it to its parent NAS. Hit <b>SNMP sync</b> to poll sysDescr, uptime and — where the vendor exposes it — the current active-ONU count.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-olt-btn" onClick={openNew} className="rounded-sm bg-primary hover:bg-primary/90"><Plus size={14} weight="bold" className="mr-2" /> New OLT</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">{editing ? `Edit OLT — ${editing.name}` : "Register OLT"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="grid grid-cols-2 gap-4">
              <F label="Name"><Input required data-testid="olt-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
              <F label="Vendor">
                <Select value={form.vendor} onValueChange={(v) => setForm({ ...form, vendor: v })}>
                  <SelectTrigger data-testid="olt-vendor"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Huawei", "ZTE", "Nokia", "Fiberhome", "Syrotech", "VSOL", "BDCOM", "Cisco", "Other"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Model"><Input required data-testid="olt-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="MA5800-X7 / C320 / 7360 …" /></F>
              <F label="Total ports"><Input required type="number" value={form.total_ports} onChange={(e) => setForm({ ...form, total_ports: e.target.value })} /></F>
              <F label="Mgmt IP (map pin)"><Input required data-testid="olt-mgmt-ip" value={form.ip_address} onChange={(e) => setForm({ ...form, ip_address: e.target.value })} placeholder="192.168.100.10" className="font-mono" /></F>
              <F label="Local IP (SNMP target)">
                <Input data-testid="olt-local-ip" value={form.local_ip} onChange={(e) => setForm({ ...form, local_ip: e.target.value })} placeholder="leave blank to reuse Mgmt IP" className="font-mono" />
              </F>
              <F label="Location" span={2}><Input required data-testid="olt-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></F>
              <F label="Latitude"><Input type="number" step="0.0001" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} /></F>
              <F label="Longitude"><Input type="number" step="0.0001" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} /></F>

              <div className="col-span-2 pt-2 border-t border-border"><div className="uppercase-label">Parent NAS &amp; SNMP</div>
                <p className="text-[11px] text-muted-foreground mt-1">Link to the BRAS/router the OLT sits behind so the topology is walkable. SNMP polls happen from the SNS backend to <span className="font-mono">Local IP:snmp_port</span>.</p>
              </div>
              <F label="Parent NAS" span={2}>
                <Select value={form.nas_id} onValueChange={(v) => setForm({ ...form, nas_id: v })}>
                  <SelectTrigger data-testid="olt-nas"><SelectValue placeholder="— none —" /></SelectTrigger>
                  <SelectContent>{nasList.map((n) => <SelectItem key={n.id} value={n.id}>{n.name} · {n.nas_ip} ({n.nas_type})</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="SNMP community"><Input data-testid="olt-community" value={form.snmp_community} onChange={(e) => setForm({ ...form, snmp_community: e.target.value })} placeholder="public" /></F>
              <F label="SNMP version">
                <Select value={form.snmp_version} onValueChange={(v) => setForm({ ...form, snmp_version: v })}>
                  <SelectTrigger data-testid="olt-snmp-ver"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="v1">SNMPv1</SelectItem><SelectItem value="v2c">SNMPv2c</SelectItem></SelectContent>
                </Select>
              </F>
              <F label="SNMP port"><Input type="number" data-testid="olt-snmp-port" value={form.snmp_port} onChange={(e) => setForm({ ...form, snmp_port: e.target.value })} placeholder="161" /></F>
              <F label="SNMP transport" span={2}>
                <label className="flex items-start gap-2 border border-border bg-secondary/30 p-3 rounded-sm cursor-pointer hover:border-primary/40 transition-colors">
                  <input
                    type="checkbox"
                    data-testid="olt-snmp-via-nas"
                    checked={!!form.snmp_via_nas}
                    onChange={(e) => setForm({ ...form, snmp_via_nas: e.target.checked })}
                    disabled={!form.nas_id}
                    className="mt-0.5"
                  />
                  <div className="text-xs">
                    <div className="font-mono font-semibold uppercase tracking-wide text-primary">Proxy SNMP through parent NAS</div>
                    <div className="text-muted-foreground mt-0.5">
                      Enable when the OLT sits on a private LAN behind a remote franchise router. SNMP GETs run through <span className="font-mono">/tool/snmp-get</span> on the NAS's RouterOS API instead of a direct UDP/161 socket from the SNS backend — no port-forwarding needed.
                      {!form.nas_id && <div className="mt-1 text-[hsl(var(--warning))]">Pick a Parent NAS above first.</div>}
                    </div>
                  </div>
                </label>
              </F>

              <DialogFooter className="col-span-2"><Button type="submit" data-testid="olt-submit" className="rounded-sm">{editing ? "Save changes" : "Register"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {olts.map((o) => {
          const util = ((o.used_ports || 0) / (o.total_ports || 1)) * 100;
          const parent = nasList.find((n) => n.id === o.nas_id);
          return (
            <div key={o.id} className="border border-border bg-card p-5 group hover:border-primary/60 transition-colors" data-testid={`olt-${o.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <HardDrives size={18} className="text-primary" />
                  <div>
                    <div className="font-display text-lg font-bold">{o.name}</div>
                    <div className="uppercase-label mt-0.5">{o.vendor} · {o.model}</div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 font-mono text-xs">
                  <span className={`pulse-dot ${o.status}`} />{o.status}
                </span>
              </div>

              <div className="mt-5 space-y-1.5 text-sm">
                <Row k="Mgmt IP" v={<span className="font-mono">{o.ip_address}</span>} />
                {o.local_ip && o.local_ip !== o.ip_address && <Row k="Local IP" v={<span className="font-mono text-primary">{o.local_ip}</span>} />}
                <Row k="Parent NAS" v={parent ? <span className="font-mono text-xs">{parent.name}</span> : <span className="italic text-muted-foreground">unlinked</span>} />
                <Row k="Location" v={<span className="text-xs">{o.location}</span>} />
                <Row k="Ports" v={<span className="font-mono">{o.used_ports}/{o.total_ports}</span>} />
                {o.active_onus !== null && o.active_onus !== undefined && <Row k="Active ONUs" v={<span className="font-mono text-primary">{o.active_onus}</span>} />}
                <Row k="SNMP" v={<span className="font-mono text-xs">{o.snmp_version || "v2c"} · {o.snmp_community || "public"} · {o.snmp_port || 161}</span>} />
              </div>

              {/* SNMP sync status */}
              <div className="mt-3 border-t border-border pt-3 text-xs" data-testid={`olt-snmp-${o.id}`}>
                {o.last_sync_at ? (
                  <>
                    <div className="flex items-center gap-2 mb-1.5">
                      {o.last_sync_ok
                        ? <CheckCircle size={14} className="text-[hsl(var(--success))]" />
                        : <XCircle size={14} className="text-[hsl(var(--destructive))]" />}
                      <span className="uppercase-label">Last sync {fmtDate(o.last_sync_at)}</span>
                    </div>
                    {o.last_sync_ok ? (
                      <div className="space-y-0.5 font-mono text-[11px] text-muted-foreground">
                        {o.sys_name && <div>name: <span className="text-foreground">{o.sys_name}</span></div>}
                        {o.sys_uptime && <div>uptime: <span className="text-foreground">{o.sys_uptime}</span></div>}
                        {o.sys_descr && <div className="truncate" title={o.sys_descr}>descr: <span className="text-foreground">{o.sys_descr}</span></div>}
                      </div>
                    ) : (
                      <div className="font-mono text-[11px] text-[hsl(var(--destructive))]">{o.last_sync_error || "unreachable"}</div>
                    )}
                  </>
                ) : (
                  <div className="text-muted-foreground italic text-[11px]">Never synced. Click SNMP sync to poll.</div>
                )}
              </div>

              <div className="mt-3">
                <div className="h-1.5 bg-border relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-primary" style={{ width: `${util}%` }} />
                </div>
                <div className="uppercase-label mt-1">Port utilisation · {util.toFixed(0)}%</div>
              </div>

              <div className="mt-4 flex justify-between items-center border-t border-border pt-3 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <button onClick={() => sync(o)} disabled={syncing[o.id]} data-testid={`olt-sync-${o.id}`} className="text-xs text-primary hover:text-primary/80 inline-flex items-center gap-1 disabled:opacity-50">
                    <ArrowsClockwise size={12} className={syncing[o.id] ? "animate-spin" : ""} /> SNMP sync
                  </button>
                  <button onClick={() => runDiagnose(o)} data-testid={`olt-diagnose-${o.id}`} className="text-xs text-[hsl(var(--warning))] hover:text-[hsl(var(--warning))]/80 inline-flex items-center gap-1" title="Probe OID roots to debug ONU walk">
                    <Stethoscope size={12} /> diagnose
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => openEdit(o)} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid={`olt-edit-${o.id}`}>
                    <PencilSimple size={12} /> edit
                  </button>
                  <button onClick={() => remove(o.id)} className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1" data-testid={`olt-del-${o.id}`}>
                    <Trash size={12} /> remove
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* SNMP Diagnose modal */}
      <Dialog open={!!diag} onOpenChange={(v) => !v && setDiag(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="snmp-diagnose-modal">
          <DialogHeader><DialogTitle className="font-display">SNMP Diagnose — {diag?.olt?.name}</DialogTitle></DialogHeader>
          {diag?.loading && <div className="text-xs text-muted-foreground font-mono">Probing OLT…</div>}
          {!diag?.loading && diag?.data && (
            <div className="space-y-4 text-xs font-mono">
              <div className="grid grid-cols-3 gap-2 border border-border p-3">
                <div><div className="uppercase-label">Host</div><div>{diag.data.host}</div></div>
                <div><div className="uppercase-label">Community</div><div>{diag.data.community}</div></div>
                <div><div className="uppercase-label">Port</div><div>{diag.data.port}</div></div>
              </div>
              {diag.data.via_nas && (
                <div className="border border-primary/40 bg-primary/10 p-3">
                  <div className="uppercase-label mb-1 text-primary">Proxied via parent NAS</div>
                  <div>RouterOS <span className="text-foreground font-semibold">{diag.data.ros_version || "?"}</span> — the SNS backend talked to the OLT through the NAS's <span className="font-mono">/tool/snmp-get</span>. Requires ROS 7.8+ for reliable readback.</div>
                  {Array.isArray(diag.data.proxy_errors) && diag.data.proxy_errors.length > 0 && (
                    <div className="mt-2 border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.15)] p-2 text-[hsl(var(--warning))]">
                      {diag.data.proxy_errors.map((e, i) => <div key={i} className="text-[11px] whitespace-pre-wrap">{e}</div>)}
                    </div>
                  )}
                </div>
              )}
              {diag.data.error && !diag.data.reachable && (
                <div className="border border-[hsl(var(--destructive)/0.4)] bg-[hsl(var(--destructive)/0.1)] p-3 text-[hsl(var(--destructive))]">
                  <div className="uppercase-label mb-1">Unreachable</div>
                  <div>{diag.data.error}</div>
                  <div className="text-muted-foreground mt-2 text-[10px]">Fix: verify OLT is powered, community string matches, and this server's IP is whitelisted in the OLT SNMP ACL.</div>
                </div>
              )}
              {diag.data.reachable && (
                <>
                  <div className="border border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)] p-3">
                    <div className="uppercase-label mb-1 text-[hsl(var(--success))]">Reachable</div>
                    <div>sysDescr: <span className="text-foreground">{diag.data.sys_descr || "—"}</span></div>
                    <div>sysName: <span className="text-foreground">{diag.data.sys_name || "—"}</span></div>
                    <div>sysObjectID: <span className="text-foreground">{diag.data.sys_object_id || "—"}</span></div>
                  </div>
                  <div className="border border-border">
                    <div className="uppercase-label p-3 border-b border-border">ONU-walk root probes</div>
                    <table className="w-full">
                      <thead className="bg-secondary/40"><tr>
                        <th className="text-left p-2 uppercase-label">Root OID</th>
                        <th className="text-left p-2 uppercase-label">Vendor</th>
                        <th className="text-right p-2 uppercase-label">Rows</th>
                        <th className="text-left p-2 uppercase-label">Note</th>
                      </tr></thead>
                      <tbody>
                        {(diag.data.probes || []).map((p, i) => (
                          <tr key={i} className="border-t border-border">
                            <td className="p-2 break-all">{p.root}</td>
                            <td className="p-2">{p.vendor}</td>
                            <td className={`p-2 text-right ${p.rows > 0 ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`}>{p.rows}</td>
                            <td className="p-2 text-[10px] text-muted-foreground truncate max-w-[220px]">{p.error || (p.rows > 0 ? `sample: ${p.sample?.[0]?.val || ""}` : "—")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(diag.data.probes || []).every((p) => p.rows === 0) && (
                    <div className="border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.1)] p-3 text-[hsl(var(--warning))] space-y-2">
                      <div className="uppercase-label">No ONU rows on any known OID</div>
                      <div>OLT answers <span className="font-mono">sysDescr / sysObjectID</span> but every ONU-table walk returns 0 rows. Most likely cause on V-SOL V1600D:</div>
                      <div className="pl-3 space-y-2 text-foreground">
                        <div className="border-l-2 border-[hsl(var(--warning))] pl-2">
                          <div><b>The public community only sees MIB-II — private community sees the vendor MIB.</b></div>
                          <div className="mt-1 text-[11px]">On many V-SOL builds the R/O community <span className="font-mono">public</span> is capped to <span className="font-mono">.1.3.6.1.2.1</span> (system). The R/W community <span className="font-mono">private</span> can read the full <span className="font-mono">.1.3.6.1.4.1.37950</span> tree — that's where the ONU rows live.</div>
                          <div className="mt-1 text-[11px]"><b>Quick test:</b> in the Custom OID box below, paste <span className="font-mono">1.3.6.1.4.1.37950</span>, set community = <span className="font-mono">private</span>, hit <b>Try walk</b>. If rows appear, edit this OLT and change <span className="font-mono">snmp_community</span> from <span className="font-mono">public</span> → <span className="font-mono">private</span>.</div>
                        </div>
                        <div className="border-l-2 border-[hsl(var(--warning))] pl-2">
                          <div><b>Or bypass with a fresh community:</b></div>
                          <div className="mt-1 text-[11px]">On V1600D web UI → <span className="font-mono">System → SNMP → Community</span>, click <b>Add Community</b>: Name = <span className="font-mono">snsread</span>, Access = <b>Read-Only</b>, Save. Then edit this OLT to use <span className="font-mono">snsread</span>. This gives you a dedicated read-only credential without exposing <span className="font-mono">private</span>.</div>
                        </div>
                        <div>Verify from Ubuntu:</div>
                        <pre className="bg-secondary/60 p-2 text-[10px] whitespace-pre-wrap break-all font-mono border border-border">snmpwalk -v2c -c private {diag.data.host} 1.3.6.1.4.1.37950 | head -20</pre>
                      </div>
                    </div>
                  )}
                  <div className="border border-border" data-testid="custom-oid-walk">
                    <div className="uppercase-label p-3 border-b border-border">Custom OID walk</div>
                    <div className="p-3 space-y-2">
                      <div className="flex gap-2">
                        <Input
                          data-testid="custom-oid-input"
                          value={customOid}
                          onChange={(e) => setCustomOid(e.target.value)}
                          placeholder="1.3.6.1.4.1.37950.1.1.3 (paste any OID root)"
                          className="font-mono text-xs flex-1"
                        />
                        <Input
                          data-testid="custom-community-input"
                          value={customCommunity}
                          onChange={(e) => setCustomCommunity(e.target.value)}
                          placeholder="community (default: OLT's)"
                          className="font-mono text-xs w-56"
                          title="Override the stored community — e.g. try 'private' if 'public' returns 0 rows on V1600D"
                        />
                        <Button
                          data-testid="custom-oid-walk-btn"
                          type="button"
                          onClick={runCustomWalk}
                          disabled={!customOid.trim() || customWalk?.loading}
                          className="rounded-sm bg-primary hover:bg-primary/90 text-xs whitespace-nowrap"
                        >
                          {customWalk?.loading ? "Walking…" : "Try walk"}
                        </Button>
                      </div>
                      {customWalk?.data?.ok && customWalk.data.rows > 0 && (
                        <div className="mt-2 flex items-center gap-2 text-[11px]">
                          <Button
                            data-testid="save-rx-oid-btn"
                            type="button"
                            className="rounded-sm bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-xs h-7"
                            onClick={async () => {
                              try {
                                await http.patch(`/olts/${diag.olt.id}`, { custom_rx_oid: customOid.trim() });
                                toast.success(`Saved ${customOid.trim()} as RX-power OID for ${diag.olt.name}. Next SNMP sync will merge dBm into ONU rows.`);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "save failed");
                              }
                            }}
                          >
                            <MagicWand size={12} className="mr-1" /> Save as RX-power OID for this OLT
                          </Button>
                          <span className="text-muted-foreground">Wires this OID into every future ONU walk — no code change needed.</span>
                        </div>
                      )}
                      {customWalk?.data && (
                        <div className="text-[11px]">
                          {customWalk.data.ok === false && (
                            <div className="text-[hsl(var(--destructive))]">Error: {customWalk.data.error}</div>
                          )}
                          {customWalk.data.ok && (
                            <>
                              <div className={customWalk.data.rows > 0 ? "text-[hsl(var(--success))]" : "text-muted-foreground"}>
                                Walked <b>{customWalk.data.rows}</b> row{customWalk.data.rows === 1 ? "" : "s"} under <span className="font-mono">{customWalk.data.root}</span>
                              </div>
                              {customWalk.data.rows > 0 && (
                                <div className="mt-2 border border-border max-h-64 overflow-y-auto">
                                  <table className="w-full text-[10px] font-mono">
                                    <thead className="bg-secondary/40"><tr>
                                      <th className="text-left p-2 uppercase-label">OID</th>
                                      <th className="text-left p-2 uppercase-label">Value</th>
                                    </tr></thead>
                                    <tbody>
                                      {(customWalk.data.sample || []).map((r, i) => (
                                        <tr key={i} className="border-t border-border">
                                          <td className="p-2 break-all">{r.oid}</td>
                                          <td className="p-2 break-all">{r.val}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, span = 1, children }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <Label className="uppercase-label">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between"><span className="text-muted-foreground">{k}</span><span>{v}</span></div>
);
